import React, { useLayoutEffect, useRef } from 'react'
import { render, unmountComponentAtNode } from 'react-dom'

import Redactor from './redactorGlobal'
import './plugins/mpMergeTags/mpMergeTags.js'
import './plugins/mpFontFamily/mpFontFamily.js'
import './plugins/mpTextFormat/mpTextFormat.js'
import './plugins/mpFontSize/mpFontSize.js'
import './plugins/mpDivider/mpDivider.js'
import './plugins/mpFontColor/mpFontColor.js'
import './plugins/mpLinkPicker/mpLinkPicker.js'
import './plugins/alignment/alignment.js'
import './plugins/emoji/emoji.js'
import './plugins/mpAnchor/mpAnchor.js'
import './plugins/mpLineHeight/mpLineHeight.js'
import './plugins/mpClearFormat/mpClearFormat.js'
import { applyExtraButtonClasses } from './plugins/registerButton'
import {
    htmlForEditor,
    htmlForEmail,
    syncEditorDom,
    patchEmptyTextBlocks,
    patchSafeCaret,
    repairEmptyEditor,
    keepSelectAllInEditor
} from './htmlForEmail'
import fontArray from '~/Classes/fonts'
import DragDropFunctions from '~/utils/TemplateEditor/dragDropFunctions'

//react parts of plugins
import MPMergeTagsDropdown from './plugins/mpMergeTags/mpMergeTagsDropdown'
import MPFontColorDropdown from './plugins/mpFontColor/mpFontColorDropdown'
import MPLinkPickerDropdown from './plugins/mpLinkPicker/mpLinkPickerDropdown'

let originalDropdownClose = null

export function stripOuterDiv(html) {
    if (!html || typeof html !== 'string') return html
    const tmp = document.createElement('div')
    tmp.innerHTML = html.trim()
    while (tmp.children.length === 1 && tmp.children[0].tagName === 'DIV') {
        tmp.innerHTML = tmp.children[0].innerHTML
    }
    return tmp.innerHTML
}

function normalizeContentForRedactor(html) {
    //at the moment redactor does not support svg so we need to normalize it
    if (!html || typeof html !== 'string') return html
    return html.replace(/<svg\b[^>]*>[\s\S]*?<\/svg>/gi, '')
}

// toolbar.target moves the toolbox out of the editor. v5 destroy() deletes app.host
// but leaves that toolbox in place, so its button tooltips throw on hover.
function removeExternalToolboxes(parentId) {
    if (!parentId) return
    const target = document.getElementById(parentId)
    if (!target) return
    target.querySelectorAll('.rx-toolbox-container').forEach((node) => node.remove())
}

// The toolbar is moved into the canvas, outside the text block. Button clicks
// already stop propagation; clicks on the bar itself do not, and the canvas
// treats those as a click away from the block.
// Leave mousedown alone. v5 uses it to mark the toolbar as blur-safe. Stopping
// it makes the editor blur when a menu opens, and that blur closes the menu
// before font colour / merge tags can render into it.
function holdToolbarClicks(parentId) {
    const target = document.getElementById(parentId)
    if (!target) return () => {}
    const stop = (event) => {
        event.stopPropagation()
    }
    target.addEventListener('click', stop)
    return () => {
        target.removeEventListener('click', stop)
    }
}

function escapeHtmlText(value) {
    return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function dropdownTriggerElement(trigger) {
    if (!trigger) return null
    if (typeof trigger.getBoundingClientRect === 'function') return trigger
    if (typeof trigger.getEl === 'function') {
        const el = trigger.getEl()
        if (el && typeof el.getBoundingClientRect === 'function') return el
    }
    return null
}

// The toolbar sits outside the text block. v5 only pins a dropdown when the
// toolbox has rx-sticky, then adds window scroll to top. Our stylesheet forces
// position:fixed, so that scroll offset drops the menu off the button.
function pinDropdownToTrigger(dropdownEl, triggerEl) {
    if (!dropdownEl || !triggerEl) return
    const triggerRect = triggerEl.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    dropdownEl.style.position = 'fixed'
    dropdownEl.style.zIndex = '1500'
    dropdownEl.style.visibility = ''
    dropdownEl.style.pointerEvents = ''

    let left = triggerRect.left
    let top = triggerRect.bottom
    const dropdownWidth = dropdownEl.offsetWidth
    const dropdownHeight = dropdownEl.offsetHeight

    if (left + dropdownWidth > viewportWidth - 10) {
        left = Math.max(viewportWidth - dropdownWidth - 10, 10)
    }
    if (left < 10) left = 10
    if (dropdownHeight && top + dropdownHeight > viewportHeight - 10) {
        top = Math.max(triggerRect.top - dropdownHeight - 4, 10)
    }

    dropdownEl.style.left = `${left}px`
    dropdownEl.style.top = `${top}px`
}

function stripOuterParagraph(html) {
    if (!html || typeof html !== 'string') return html
    const tmp = document.createElement('div')
    tmp.innerHTML = html.trim()
    if (tmp.children.length === 1 && tmp.children[0].tagName === 'P') {
        return tmp.children[0].innerHTML
    }
    return html
}

const RedactorJS = ({
    opts = {},
    content = '',
    updateContent = () => {},
    floatingToolBarParent = 'templatBodyBuilderFloatingWysiToolbar',
    defaultFont = 'Arial', //Default font to use for the font family plugin
    defaultColor = '#000000', //Default color to use for the font color plugin
    globalColours = [], //Global colours to use for the font color plugin
    favoriteColors = [], //Favorite colors to use for the font color plugin
    elementsData = [], //Elements data to use for the link picker plugin
    elementUuid = null, //Unique identifier for the editor app
    allowParagraphs = true,
    noOuterP = false,
    noOuterDiv = false,
    overrideTools = [],
    overridePlugins = undefined,
    campaign = {},
    isLandingPage = false,
    overrideInlinePopup = [],
    overrideMpTextFormatExcludedFormats = []
}) => {
    const textAreaRef = useRef(null)

    const dropdownElementRef = useRef(null)
    const elementsDataRef = useRef(elementsData)
    elementsDataRef.current = elementsData
    let editorApp

    const emojiOverride = {
        trigger: false, //Turns off : in editor to bring up emoji picker inline
        items: {
            // Replaces the default preset list entirely
            set: true,

            // Optional: seed a small “top row” in the picker
            favorites: {
                thumbsUp: '👍',
                redHeart: '❤️',
                partyPopper: '🎉',
                sparkle: '✨',
                fire: '🔥',
                checkMark: '✅',
                warning: '⚠️',
                thinkingFace: '🤔',
                faceTearsJoy: '😂',
                loudlyCryingFace: '😭',
                grinningFace: '😀',
                smilingFaceHearts: '🥰'
            },

            // Smileys & Emotion
            smileys: {
                grinningFace: '😀',
                grinningFaceBigEyes: '😃',
                grinningFaceSmilingEyes: '😄',
                beamingFaceSmilingEyes: '😁',
                grinningSquintingFace: '😆',
                grinningFaceSweat: '😅',
                rollingOnFloorLaughing: '🤣',
                faceTearsJoy: '😂',
                slightlySmilingFace: '🙂',
                upsideDownFace: '🙃',
                winkingFace: '😉',
                smilingFaceSmilingEyes: '😊',
                smilingFaceHalo: '😇',
                smilingFaceHearts: '🥰',
                smilingFaceHeartEyes: '😍',
                starStruck: '🤩',
                faceBlowingKiss: '😘',
                kissingFace: '😗',
                kissingFaceSmilingEyes: '😙',
                kissingFaceClosedEyes: '😚',
                faceSavoringFood: '😋',
                faceTongue: '😛',
                winkingFaceTongue: '😜',
                zanyFace: '🤪',
                squintingFaceTongue: '😝',
                moneyMouthFace: '🤑',
                huggingFace: '🤗',
                faceHandOverMouth: '🤭',
                shushingFace: '🤫',
                thinkingFace: '🤔',
                zipperMouthFace: '🤐',
                neutralFace: '😐',
                expressionlessFace: '😑',
                faceWithoutMouth: '😶',
                smirkingFace: '😏',
                unamusedFace: '😒',
                faceRollingEyes: '🙄',
                grimacingFace: '😬',
                lyingFace: '🤥',
                relievedFace: '😌',
                pensiveFace: '😔',
                sleepyFace: '😪',
                droolingFace: '🤤',
                sleepingFace: '😴',
                faceMedicalMask: '😷',
                faceThermometer: '🤒',
                faceHeadBandage: '🤕',
                nauseatedFace: '🤢',
                faceVomiting: '🤮',
                sneezingFace: '🤧',
                hotFace: '🥵',
                coldFace: '🥶',
                woozyFace: '🥴',
                dizzyFace: '😵',
                explodingHead: '🤯',
                cowboyHatFace: '🤠',
                partyingFace: '🥳',
                faceSunglasses: '😎',
                nerdFace: '🤓',
                faceMonocle: '🧐',
                confusedFace: '😕',
                worriedFace: '😟',
                slightlyFrowningFace: '🙁',
                frowningFace: '☹️',
                faceOpenMouth: '😮',
                hushedFace: '😯',
                astonishedFace: '😲',
                flushedFace: '😳',
                pleadingFace: '🥺',
                frowningFaceOpenMouth: '😦',
                anguishedFace: '😧',
                fearfulFace: '😨',
                anxiousFaceSweat: '😰',
                sadButRelievedFace: '😥',
                cryingFace: '😢',
                loudlyCryingFace: '😭',
                faceScreamingFear: '😱',
                confoundedFace: '😖',
                perseveringFace: '😣',
                disappointedFace: '😞',
                downcastFaceSweat: '😓',
                wearyFace: '😩',
                tiredFace: '😫',
                yawningFace: '🥱',
                faceSteamNose: '😤',
                poutingFace: '😡',
                angryFace: '😠',
                faceSymbolsMouth: '🤬',
                smilingFaceHorns: '😈',
                angryFaceHorns: '👿',
                skull: '💀',
                skullCrossbones: '☠️',
                pileOfPoo: '💩',
                clownFace: '🤡',
                ogre: '👹',
                goblin: '👺',
                ghost: '👻',
                alien: '👽',
                robot: '🤖'
            },

            // People & Gestures (no skin tone variants)
            people: {
                wavingHand: '👋',
                raisedHand: '✋',
                okHand: '👌',
                pinchHand: '🤏',
                victoryHand: '✌️',
                crossedFingers: '🤞',
                signOfTheHorns: '🤘',
                callMeHand: '🤙',
                backhandIndexPointingLeft: '👈',
                backhandIndexPointingRight: '👉',
                backhandIndexPointingUp: '👆',
                backhandIndexPointingDown: '👇',
                indexPointingUp: '☝️',
                thumbsUp: '👍',
                thumbsDown: '👎',
                clappingHands: '👏',
                raisingHands: '🙌',
                openHands: '👐',
                handshake: '🤝',
                foldedHands: '🙏',
                writingHand: '✍️',
                flexedBiceps: '💪',
                selfie: '🤳',
                ear: '👂',
                nose: '👃',
                eyes: '👀',
                brain: '🧠',
                tooth: '🦷',
                tongue: '👅',
                mouth: '👄'
            },

            // Animals & Nature
            animals: {
                dogFace: '🐶',
                catFace: '🐱',
                mouseFace: '🐭',
                hamster: '🐹',
                rabbitFace: '🐰',
                fox: '🦊',
                bear: '🐻',
                panda: '🐼',
                koala: '🐨',
                tigerFace: '🐯',
                lion: '🦁',
                cowFace: '🐮',
                pigFace: '🐷',
                frog: '🐸',
                monkeyFace: '🐵',
                chicken: '🐔',
                penguin: '🐧',
                bird: '🐦',
                chickHatching: '🐣',
                owl: '🦉',
                butterfly: '🦋',
                ladyBeetle: '🐞',
                honeybee: '🐝',
                snail: '🐌',
                octopus: '🐙',
                fish: '🐟',
                tropicalFish: '🐠',
                dolphin: '🐬',
                whale: '🐳',
                crocodile: '🐊',
                turtle: '🐢',
                snake: '🐍',
                dragon: '🐉',
                cactus: '🌵',
                christmasTree: '🎄',
                evergreenTree: '🌲',
                deciduousTree: '🌳',
                palmTree: '🌴',
                seedling: '🌱',
                herb: '🌿',
                shamrock: '☘️',
                fourLeafClover: '🍀',
                sunflower: '🌻',
                rose: '🌹',
                tulip: '🌷',
                blossom: '🌼',
                hibiscus: '🌺',
                mapleLeaf: '🍁',
                fallenLeaf: '🍂',
                leafFlutteringWind: '🍃',
                globeShowingEuropeAfrica: '🌍',
                globeShowingAmericas: '🌎',
                globeShowingAsiaAustralia: '🌏',
                sun: '☀️',
                cloud: '☁️',
                umbrella: '☂️',
                snowflake: '❄️',
                comet: '☄️',
                fire: '🔥',
                droplet: '💧',
                waterWave: '🌊'
            },

            // Food & Drink
            food: {
                greenSalad: '🥗',
                avocado: '🥑',
                tomato: '🍅',
                carrot: '🥕',
                earOfCorn: '🌽',
                hotPepper: '🌶️',
                cucumber: '🥒',
                mushroom: '🍄',
                bread: '🍞',
                croissant: '🥐',
                cheeseWedge: '🧀',
                egg: '🥚',
                bacon: '🥓',
                pancakes: '🥞',
                pizza: '🍕',
                hamburger: '🍔',
                fries: '🍟',
                taco: '🌮',
                burrito: '🌯',
                sushi: '🍣',
                bentoBox: '🍱',
                riceBall: '🍙',
                cookedRice: '🍚',
                spaghetti: '🍝',
                curryRice: '🍛',
                ramen: '🍜',
                dumpling: '🥟',
                sandwich: '🥪',
                tacoSalad: '🥙',
                popcorn: '🍿',
                doughnut: '🍩',
                cookie: '🍪',
                birthdayCake: '🎂',
                shortcake: '🍰',
                cupcake: '🧁',
                chocolateBar: '🍫',
                candy: '🍬',
                lollipop: '🍭',
                honeyPot: '🍯',
                iceCream: '🍨',
                shavedIce: '🍧',
                softIceCream: '🍦',
                pie: '🥧',
                apple: '🍎',
                greenApple: '🍏',
                pear: '🍐',
                peach: '🍑',
                cherries: '🍒',
                strawberry: '🍓',
                watermelon: '🍉',
                pineapple: '🍍',
                banana: '🍌',
                lemon: '🍋',
                grapes: '🍇',
                kiwi: '🥝',
                mango: '🥭',
                coconut: '🥥',
                coffee: '☕',
                tea: '🍵',
                milk: '🥛',
                beerMug: '🍺',
                clinkingGlasses: '🥂',
                wineGlass: '🍷',
                cocktailGlass: '🍸'
            },

            // Travel & Places
            travel: {
                rocket: '🚀',
                airplane: '✈️',
                smallAirplane: '🛩️',
                helicopter: '🚁',
                sailboat: '⛵',
                ship: '🚢',
                automobile: '🚗',
                taxi: '🚕',
                bus: '🚌',
                policeCar: '🚓',
                ambulance: '🚑',
                fireEngine: '🚒',
                bicycle: '🚲',
                motorScooter: '🛵',
                fuelPump: '⛽',
                horizontalTrafficLight: '🚥',
                construction: '🚧',
                stopSign: '🛑',
                mapOfWorld: '🗺️',
                compass: '🧭',
                mountain: '⛰️',
                snowCappedMountain: '🏔️',
                volcano: '🌋',
                camping: '🏕️',
                beachUmbrella: '🏖️',
                desert: '🏜️',
                cityscape: '🏙️',
                sunrise: '🌅',
                sunset: '🌇',
                nightWithStars: '🌃',
                ferrisWheel: '🎡',
                rollerCoaster: '🎢',
                stadium: '🏟️',
                house: '🏠',
                officeBuilding: '🏢',
                hospital: '🏥',
                school: '🏫',
                hotel: '🏨',
                loveHotel: '🏩'
            },

            // Activity
            activities: {
                soccerBall: '⚽',
                basketball: '🏀',
                volleyball: '🏐',
                tennis: '🎾',
                baseball: '⚾',
                cricketGame: '🏏',
                rugbyFootball: '🏉',
                bowling: '🎳',
                chessPawn: '♟️',
                videoGame: '🎮',
                joystick: '🕹️',
                gameDie: '🎲',
                musicalNote: '🎵',
                microphone: '🎤',
                headphones: '🎧',
                saxophone: '🎷',
                guitar: '🎸',
                drum: '🥁',
                clapperBoard: '🎬',
                artistPalette: '🎨',
                performingArts: '🎭',
                ticket: '🎫',
                trophy: '🏆',
                medal: '🏅'
            },

            // Objects
            objects: {
                lightBulb: '💡',
                flashlight: '🔦',
                candle: '🕯️',
                laptop: '💻',
                desktopComputer: '🖥️',
                keyboard: '⌨️',
                computerMouse: '🖱️',
                mobilePhone: '📱',
                telephone: '☎️',
                camera: '📷',
                movieCamera: '🎥',
                television: '📺',
                radio: '📻',
                hourglass: '⌛',
                alarmClock: '⏰',
                watch: '⌚',
                battery: '🔋',
                electricPlug: '🔌',
                magnifyingGlass: '🔍',
                lock: '🔒',
                unlocked: '🔓',
                key: '🔑',
                hammer: '🔨',
                wrench: '🔧',
                screwdriver: '🪛',
                nutAndBolt: '🔩',
                gear: '⚙️',
                toolbox: '🧰',
                scissors: '✂️',
                pencil: '✏️',
                pen: '🖊️',
                paintbrush: '🖌️',
                notebook: '📓',
                ledger: '📒',
                books: '📚',
                bookmark: '🔖',
                label: '🏷️',
                envelope: '✉️',
                incomingEnvelope: '📨',
                package: '📦',
                clipboard: '📋',
                calendar: '📅',
                chartIncreasing: '📈',
                chartDecreasing: '📉',
                barChart: '📊',
                pushpin: '📌',
                paperclip: '📎',
                trashCan: '🗑️',
                moneyBag: '💰',
                creditCard: '💳',
                receipt: '🧾',
                shoppingCart: '🛒',
                gift: '🎁',
                balloon: '🎈',
                partyPopper: '🎉',
                confettiBall: '🎊'
            },

            // Symbols (keep practical, avoid weird “special char” sets)
            symbols: {
                checkMark: '✅',
                crossMark: '❌',
                warning: '⚠️',
                prohibited: '🚫',
                info: 'ℹ️',
                questionMark: '❓',
                exclamationMark: '❗',
                redHeart: '❤️',
                orangeHeart: '🧡',
                yellowHeart: '💛',
                greenHeart: '💚',
                blueHeart: '💙',
                purpleHeart: '💜',
                blackHeart: '🖤',
                sparkle: '✨',
                star: '⭐',
                glowingStar: '🌟',
                collision: '💥',
                hundredPoints: '💯',
                checkBox: '☑️',
                radioButton: '🔘',
                rightArrow: '➡️',
                leftArrow: '⬅️',
                upArrow: '⬆️',
                downArrow: '⬇️',
                repeat: '🔁',
                refresh: '🔄',
                plus: '➕',
                minus: '➖',
                multiply: '✖️',
                divide: '➗'
            }
        }
    }

    const inlineMenu =
        Array.isArray(overrideInlinePopup) && overrideInlinePopup.length > 0
            ? overrideInlinePopup
            : ['sup', 'sub', 'deleted', 'highlight', 'removeinline', 'mpAnchorList']

    // Redactor starts with a real textarea and moves its controls into a shared toolbar target.
    // A normal effect runs after paint, so the raw textarea briefly participates in the selected
    // element's layout and stretches it before Redactor replaces it. The shared toolbar can also
    // appear empty while the previous editor is destroyed and the selected editor is initialized.
    // React runs layout-effect cleanup and setup synchronously before the browser can paint, so the
    // intermediate textarea and empty-toolbar DOM exists briefly in memory but never reaches the screen.
    useLayoutEffect(() => {
        const storedContent = content
        content = htmlForEditor(normalizeContentForRedactor(content))
        let lastPublished = storedContent
        let toolBarButtons = [
            'mpDivider1',
            'mpTextFormat',
            'mpDivider2',
            'mpFontFamily',
            'mpDivider3',
            'mpFontSize',
            'mpDivider4',
            'mpFontColor',
            'bold',
            'underline',
            'italic',
            // "underline",
            'mpLinkPicker',
            'mpDivider5',
            'alignment',
            'list',
            'mpLineHeight',
            'mpDivider6',
            'emoji',
            'source',
            'mpClearFormat',
            'mpAnchor',
            'mpDivider7',
            'mpMergeTags',
            'mpDivider8',
            'moreinline'
        ]

        let toolBarPlugins = [
            'mpMergeTags',
            'mpFontFamily',
            'mpTextFormat',
            'mpFontSize',
            'mpDivider',
            'mpFontColor',
            'mpLinkPicker',
            'emoji',
            'mpAnchor',
            'alignment',
            'mpClearFormat',
            'mpLineHeight'
        ]

        if (overrideTools.length > 0) {
            toolBarButtons = overrideTools
        }

        if (overridePlugins) {
            toolBarPlugins = overridePlugins
        }

        let noDividers = toolBarButtons.filter((button) => button.indexOf('mpDivider') === 0).length

        let markup = 'p'
        if (!allowParagraphs) {
            markup = 'div'
        }
        const currentDropdownItems = () => {
            const root = editorApp && editorApp.dropdown && editorApp.dropdown.getEl()
            return root ? root.querySelector('.rx-dropdown-items') : null
        }
        const pinOpenDropdown = () => {
            if (!editorApp || !editorApp.dropdown || !editorApp.dropdown.isOpen()) return
            const trigger = dropdownTriggerElement(editorApp.dropdown.trigger)
            if (trigger) pinDropdownToTrigger(editorApp.dropdown.getEl(), trigger)
        }

        const publishHtml = (html) => {
            let cleanedContent = htmlForEmail(normalizeContentForRedactor(html))
            if (noOuterP) cleanedContent = stripOuterParagraph(cleanedContent)
            if (noOuterDiv) cleanedContent = stripOuterDiv(cleanedContent)
            if (cleanedContent === lastPublished) return
            lastPublished = cleanedContent
            updateContent(cleanedContent)
        }

        // editor:change is debounced (300ms). Clicking out destroys the editor
        // before that timer, so read the live DOM here.
        const flushLiveContent = () => {
            if (!editorApp) return
            if (editorApp.source && typeof editorApp.source.isVisible === 'function' && editorApp.source.isVisible()) {
                publishHtml(editorApp.source.getValue())
                return
            }
            if (typeof editorApp.getHtml === 'function') publishHtml(editorApp.getHtml())
        }

        const syncContentFromEditor = function () {
            publishHtml(this.getHtml())
        }

        let redactorOpts = {
            theme: 'light',
            minHeight: '1.2em',
            content: content,
            nostyle: true,
            nocontainer: true,
            clean: {
                enter: false,
                // Match htmlForEditor: empty blocks keep a br, not nbsp, while editing.
                keepEmptyBlockBr: true
            },
            paste: {
                sanitize: true,
                blockTags: ['div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li'],
                inlineTags: [],
                keepAttrs: [],
                images: false
            },
            plugins: toolBarPlugins,
            emoji: emojiOverride,
            active: {
                tags: {
                    a: ['mpLinkPicker']
                }
            },
            buttons: {
                toolbar: toolBarButtons,
                extrabar: []
            },
            // The floating toolbar is the only formatting UI. v5 otherwise
            // shows this same set again as a black bar on the selection.
            context: false,
            // v5 hides the native caret and draws .rx-visual-caret. Those
            // styles are not in the stylesheet this page loads, so the cursor
            // only appears when Redactor falls back to the native caret.
            visualCaret: false,
            dropdown: {
                format: ['text', 'h1', 'h2', 'h3', 'h4'],
                inline: inlineMenu,
                list: ['bulletlist', 'numberedlist'],
                haslist: ['bulletlist', 'numberedlist', 'indent', 'outdent']
            },
            markup: markup,
            mpMergeTags: {
                generateMergeTags: async (elementName) => {
                    const dropdown = currentDropdownItems()
                    if (!dropdown) {
                        return
                    }
                    render(
                        <MPMergeTagsDropdown
                            selectTag={(content) => {
                                editorApp.dropdown.close()
                                insertContent(content)
                            }}
                            isLandingPage={isLandingPage}
                            campaign={campaign}
                        />,
                        dropdown
                    )
                    dropdownElementRef.current = dropdown
                    pinOpenDropdown()
                }
            },
            mpFontColor: {
                defaultColor: 'inherit',
                generateColorPicker: async (elementName, currentColor) => {
                    const dropdown = currentDropdownItems()
                    if (!dropdown) {
                        return
                    }
                    render(
                        <MPFontColorDropdown
                            elementsData={elementsDataRef.current}
                            globalColours={globalColours}
                            favoriteColors={favoriteColors}
                            color={currentColor || defaultColor}
                            editorApp={editorApp}
                            onChange={(color, instant = false) => {
                                editorApp.invoke('mpFontColor.updateColor', color, instant)
                            }}
                        />,
                        dropdown
                    )
                    dropdownElementRef.current = dropdown
                    pinOpenDropdown()
                }
            },
            mpFontFamily: {
                fonts: fontArray,
                getFonts: () => DragDropFunctions.getFontArrayWithBrandFonts(),
                groupOrder: DragDropFunctions.FONT_SELECT_GROUP_ORDER,
                defaultFont: defaultFont
            },
            mpTextFormat: {
                excludedFormats: overrideMpTextFormatExcludedFormats
            },
            mpLinkPicker: {
                generateLinkPicker: async (elementName) => {
                    const dropdown = currentDropdownItems()
                    if (!dropdown) {
                        return
                    }

                    const selection = editorApp.getService('selection')
                    const linkNodes = selection.getNodes({ tags: ['a'] })
                    let linkValue = ''
                    if (linkNodes.length > 0) {
                        linkValue = linkNodes[0].getAttribute('href')
                    }
                    editorApp.saveSelection()

                    render(
                        <MPLinkPickerDropdown
                            elementsData={elementsDataRef.current}
                            isLandingPage={isLandingPage}
                            value={linkValue}
                            onClose={() => {
                                editorApp.dropdown.close()
                                editorApp.restoreSelection()
                            }}
                            onUpdate={(newLinkValue) => {
                                editorApp.dropdown.close()
                                editorApp.restoreSelection()
                                insertUpdateLink(newLinkValue)
                            }}
                        />,
                        dropdown
                    )
                    dropdownElementRef.current = dropdown
                    pinOpenDropdown()
                }
            },
            mpDivider: {
                noDividers: noDividers
            },
            anchor: {
                icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="fill: none !important;">
          <path d="M12 20V8.79999" stroke="#0A0A0A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M6.4 12H4C4 14.1217 4.84285 16.1566 6.34315 17.6569C7.84344 19.1571 9.87827 20 12 20C14.1217 20 16.1566 19.1571 17.6569 17.6569C19.1571 16.1566 20 14.1217 20 12H17.6" stroke="#0A0A0A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M11.9996 8.8C13.3251 8.8 14.3996 7.72548 14.3996 6.4C14.3996 5.07452 13.3251 4 11.9996 4C10.6741 4 9.59961 5.07452 9.59961 6.4C9.59961 7.72548 10.6741 8.8 11.9996 8.8Z" stroke="#0A0A0A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>`
            },
            buttonsObj: {
                deleted: {
                    title: 'Strikethrough'
                }
            },
            events: {
                'editor:change': syncContentFromEditor,
                // source.change has no v5 counterpart; flush HTML when leaving source mode
                'source:hide': syncContentFromEditor
            },
            focus: true
        }

        let releaseToolbarClicks = () => {}
        let releaseEmptyTextBlockPatch = () => {}
        let releaseSafeCaret = () => {}
        let repairAfterDelete = () => {}
        let editableForSelectAll = null
        let onSelectAllKey = () => {}
        if (floatingToolBarParent) {
            removeExternalToolboxes(floatingToolBarParent)
            releaseToolbarClicks = holdToolbarClicks(floatingToolBarParent)
            redactorOpts.toolbar = {
                target: '#' + floatingToolBarParent
            }
        }
        if (textAreaRef.current) {
            editorApp = Redactor(textAreaRef.current, redactorOpts)
            releaseEmptyTextBlockPatch = patchEmptyTextBlocks(editorApp)
            releaseSafeCaret = patchSafeCaret(editorApp)
            if (editorApp?.toolbar?.buildToolbar) {
                const buttonsObj = editorApp.getOption('buttonsObj') || {}
                editorApp.toolbar.dynamicButtons.forEach((cfg, name) => {
                    if (!name || buttonsObj[name]) return
                    const rest = { ...(cfg || {}), name }
                    delete rest.position
                    if (typeof rest.classname === 'string') {
                        rest.classname = rest.classname.split(/\s+/).filter(Boolean)[0] || ''
                    }
                    buttonsObj[name] = rest
                })
                Object.keys(buttonsObj).forEach((name) => {
                    const cfg = buttonsObj[name]
                    if (!cfg || typeof cfg.classname !== 'string' || !/\s/.test(cfg.classname)) return
                    cfg.classname = cfg.classname.split(/\s+/).filter(Boolean)[0] || ''
                })
                editorApp.toolbar.buildToolbar()
                applyExtraButtonClasses(editorApp)
                editorApp.on('block:focus', () => applyExtraButtonClasses(editorApp))
                editorApp.on('selection:done', () => applyExtraButtonClasses(editorApp))
            }

            const keepEditorDom = () => syncEditorDom(editorApp)
            repairAfterDelete = (payload) => {
                const key = payload && payload.event && payload.event.key
                if (key && key !== 'Backspace' && key !== 'Delete') return
                repairEmptyEditor(editorApp)
            }
            keepEditorDom()
            editorApp.on('parser:loaded', keepEditorDom)
            editorApp.on('content:input', keepEditorDom)
            editorApp.on('editor:change', keepEditorDom)
            editorApp.on('content:input', repairAfterDelete)
            editorApp.on('key:up', repairAfterDelete)

            editableForSelectAll = editorApp.getEditor && editorApp.getEditor()
            onSelectAllKey = (event) => keepSelectAllInEditor(event, editableForSelectAll)
            if (editableForSelectAll) {
                editableForSelectAll.addEventListener('keydown', onSelectAllKey, true)
            }

            let origcreate = editorApp.dropdown.create
            editorApp.dropdown.create = function (...args) {
                closeDropDown()
                return origcreate.apply(this, args)
            }

            let origOpen = editorApp.dropdown.open
            editorApp.dropdown.open = function (trigger) {
                const result = origOpen.apply(this, arguments)
                if (!floatingToolBarParent || !this.isOpen || !this.isOpen() || !this.dropdown) return result
                const triggerEl = dropdownTriggerElement(this.trigger) || dropdownTriggerElement(trigger)
                if (!triggerEl) return result
                const dropdownEl = this.dropdown
                pinDropdownToTrigger(dropdownEl, triggerEl)
                requestAnimationFrame(() => {
                    if (!this.isOpen || !this.isOpen()) return
                    pinDropdownToTrigger(dropdownEl, triggerEl)
                })
                return result
            }

            const origUpdatePosition = editorApp.dropdown.updatePosition
            if (typeof origUpdatePosition === 'function') {
                editorApp.dropdown.updatePosition = function () {
                    const result = origUpdatePosition.apply(this, arguments)
                    if (!floatingToolBarParent || !this.isOpen || !this.isOpen() || !this.dropdown) return result
                    const triggerEl = dropdownTriggerElement(this.trigger)
                    if (triggerEl) pinDropdownToTrigger(this.dropdown, triggerEl)
                    return result
                }
            }

            // Override the built-in dropdown.close function
            if (editorApp && editorApp.dropdown && editorApp.dropdown.close) {
                originalDropdownClose = editorApp.dropdown.close.bind(editorApp.dropdown)
                editorApp.dropdown.close = function (opts) {
                    // Clicking the external toolbar blurs the text block. v5 treats
                    // that blur as "clicked away" and closes the menu in the same
                    // turn the plugin was about to render into it.
                    if (opts && opts.reason === 'blur') {
                        const triggerEl = dropdownTriggerElement(this.trigger)
                        const toolbarHost = floatingToolBarParent && document.getElementById(floatingToolBarParent)
                        const active = document.activeElement
                        const openedFromToolbar = Boolean(triggerEl && toolbarHost && toolbarHost.contains(triggerEl))
                        const focusInDropdown = Boolean(this.dropdown && active && this.dropdown.contains(active))
                        if (openedFromToolbar || focusInDropdown) return
                    }
                    closeDropDown()
                    return originalDropdownClose.apply(this, arguments)
                }
            }

            // Clicks in link-picker drawers sit outside the Redactor dropdown.
            // v5 only binds the outside-click listener when a dropdown opens, so
            // wrap that bind rather than the handler (it does not exist yet).
            const outsideClickSelectors = [
                '.uiDrawer',
                '.uiDrawerInner',
                '.mpzLinkPicker',
                '.templateEditor-redactorplugins-mpLinkPicker',
                '.rx-option-list',
                '.cyt-cross-drawer',
                '.dropdown-button-select-dropDownInner',
                '.topnav-dropdown',
                '.filemanager-tile-controls'
            ].join(',')
            const dropdown = editorApp.dropdown
            const toolbarHost = floatingToolBarParent ? document.getElementById(floatingToolBarParent) : null
            if (dropdown && typeof dropdown._startEvents === 'function') {
                const originalStartEvents = dropdown._startEvents
                dropdown._startEvents = function () {
                    originalStartEvents.call(dropdown)
                    const originalMouseUp = dropdown._onDocMouseUp
                    const pointerDocs = dropdown._pointerDocs
                    if (!originalMouseUp || !pointerDocs || !pointerDocs.length) return
                    const wrappedMouseUp = function (e) {
                        const node = e.target && e.target.nodeType === 1 ? e.target : e.target && e.target.parentElement
                        if (node && toolbarHost && toolbarHost.contains(node)) return
                        if (node && node.closest && node.closest(outsideClickSelectors)) return
                        return originalMouseUp.call(dropdown, e)
                    }
                    pointerDocs.forEach((doc) => {
                        doc.removeEventListener('mouseup', originalMouseUp, true)
                        doc.addEventListener('mouseup', wrappedMouseUp, true)
                    })
                    dropdown._onDocMouseUp = wrappedMouseUp
                }
            }
        }

        return () => {
            releaseToolbarClicks()
            releaseEmptyTextBlockPatch()
            releaseSafeCaret()
            if (editableForSelectAll) {
                editableForSelectAll.removeEventListener('keydown', onSelectAllKey, true)
            }
            if (editorApp) {
                try {
                    flushLiveContent()
                } catch (err) {
                    // Still destroy the editor if the final read fails.
                }
                editorApp.off('content:input', repairAfterDelete)
                editorApp.off('key:up', repairAfterDelete)
                if (editorApp.dropdown && originalDropdownClose) {
                    //restore original dropdown.close
                    editorApp.dropdown.close = originalDropdownClose
                    originalDropdownClose = null
                }
                closeDropDown()
                // context: false skips building the bar, but v5 stop() still
                // writes wrapper.style and throws when the editor unmounts.
                if (editorApp.context && !editorApp.context.wrapper) {
                    editorApp.context.stop = function () {}
                }
                removeExternalToolboxes(floatingToolBarParent)
                editorApp.destroy()
                editorApp = null
            }
        }
    }, [])

    const closeDropDown = () => {
        if (!dropdownElementRef?.current) {
            return
        }
        let dropDownElement = dropdownElementRef.current

        if (dropDownElement && dropDownElement._reactRootContainer) {
            try {
                unmountComponentAtNode(dropDownElement)
            } catch (error) {
                //error handling here in a bit if needed
            }
        } else {
            //no react component detected on element
        }

        dropDownElement = null
    }

    const insertContent = (content) => {
        if (editorApp) {
            editorApp.restoreSelection()
            editorApp.insertHtml(content)
        }
    }

    const insertUpdateLink = (newLinkValue) => {
        if (!editorApp) return

        const linkManager = editorApp.link
        const existingLinks = linkManager.get()

        if (!newLinkValue) {
            linkManager.unlink()
            return
        }
        const url = String(newLinkValue).trim()
        if (!url || /^javascript:/i.test(url)) return
        if (existingLinks.length > 0) {
            linkManager.update({ url, target: '_blank' })
            return
        }
        // link.create writes textContent and drops bold/colour inside the selection.
        const formatter = editorApp.getService('inlineFormatter')
        const selection = editorApp.getService('selection')
        const range = selection && selection.getRange && selection.getRange()
        if (formatter && range && !range.collapsed) {
            formatter.format({
                tag: 'a',
                attrs: { href: url, target: '_blank', rel: 'noopener noreferrer' }
            })
            return
        }
        const label = escapeHtmlText((selection && selection.getText && selection.getText()) || url)
        editorApp.insertHtml(`<a href="${escapeHtmlText(url)}" target="_blank" rel="noopener noreferrer">${label}</a>`)
    }

    return (
        <>
            <textarea ref={textAreaRef} id={`redactor-${elementUuid}`}></textarea>
        </>
    )
}

export default RedactorJS
